import React, {useEffect, useMemo, useState} from 'react';
import {Box, Grid} from '@mui/material';
import {get} from 'lodash';
import {
  ActionType,
  AutoField,
  SelectGroupOption,
  SelectOption,
} from '../../../components/AutoForm/AutoFieldNew';
import usePlanNamesProviders from '../../../hooks/usePlanNamesProviders';
import {useAccountContext} from '../../../contexts/AccountContext';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import type {newPlanFields} from './setUpNewFields';
import {
  setUpNewPlanProviderField as recommendPlanProviderField,
  setUpNewPlanTypeField as recommendPlanTypeField,
  inTrustField,
  investorCategoryField,
} from './setUpNewFields';
import type {modelPortfolioField} from '../RecommendModify/modifyFieldTypes';
import {withdrawalAmountField as recommendAmountField} from '../RecommendsWithdrawal/withdrawalFields';
import {MandatesWrapper} from '../../../pages/MandatesPage/MandatesModal';
import {hasWrapper} from '../CreateRecommend/RecommendSourceDestination/utils';
import {
  updateNewPlanProducts,
  updateNewPlanProviders,
  updateIsSaltusProvider,
  deriveIsNewLisa,
  setDefaultSaltusSippPlanType,
  clearMandateOnProviderBlur,
} from '../hooks/setUpNewHooks';
import {
  AutoFieldHelpers,
  useAutoForm,
  useAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {useSPPProviders} from '../CreateRecommend/useSPPProviders';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import {PlanFeesField} from '../CreateRecommend/PlanFees';
import {includesSaltus} from '../../../utils/includesSaltus';
import {sanitiseProductType} from '../../../utils/sanitiseProductType';
import validateUUID from '../../../utils/validateUUID';
import {ParentGuardianSearch} from '../../../components/ParentGuardianSearch/ParentGuardianSearch';
import {useIsMinor} from '../hooks/createRecommendHooks';

export interface NewPlanFormProps {
  formikKeyPrefix: string;
  isLowesPension: boolean;
  isTransactBond: boolean;
}

export {
  updateIsSaltusProvider,
  deriveIsNewLisa,
  setDefaultSaltusSippPlanType,
  clearMandateOnProviderBlur,
};

export const useUpdateIsSaltusProvider = ({
  isSaltusProviderHelpers,
  productProviders,
  planProvider,
}: {
  isSaltusProviderHelpers: AutoFieldHelpers<boolean>;
  productProviders: ReturnType<typeof usePlanNamesProviders>['productProviders'];
  planProvider: string | undefined;
}) => {
  useEffect(() => {
    updateIsSaltusProvider({isSaltusProviderHelpers, productProviders, planProvider});
  }, [planProvider, productProviders]);
};

export const useSetDefaultSaltusSippPlanType = ({
  displayProductNames,
  isSaltusSIPP,
  formikKeyPrefix,
}: {
  displayProductNames: SelectOption[] | undefined;
  isSaltusSIPP: boolean;
  formikKeyPrefix: string;
}) => {
  const {setFieldValue} = useAutoForm<recommendFieldsType>();
  useEffect(() => {
    setDefaultSaltusSippPlanType({
      displayProductNames,
      isSaltusSIPP,
      setFieldValue,
      formikKeyPrefix,
    });
  }, [isSaltusSIPP, displayProductNames]);
};

export const NewPlanForm: React.FC<NewPlanFormProps> = ({
  formikKeyPrefix,
  isLowesPension,
  isTransactBond,
}) => {
  const {accountId} = useAccountContext();
  const isMinor = useIsMinor();
  const {
    productNames,
    productProviders,
    rawProviders,
    queryGetProductProviders,
    queryGetProducts,
  } = usePlanNamesProviders();

  const [mandateField, mandateMeta, mandateHelpers] = useAutoField<
    modelPortfolioField | undefined
  >(`${formikKeyPrefix}.model`);
  const {isSPPDA} = useRecommendContext();
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const {mainPlanType} = values;
  const needsInTrust = useMemo(() => mainPlanType === 'gia', [mainPlanType]);
  const [mainPlanTypeField] =
    useAutoField<recommendFieldsType['mainPlanType']>('mainPlanType');
  const [newPlanFieldObj] = useAutoField<newPlanFields>(formikKeyPrefix);
  const [isSaltusProviderFieldObj, isSaltusProviderMeta, isSaltusProviderHelpers] =
    useAutoField<boolean>('contribute.destinationValues.isSaltusProvider');

  const [productProvidersOptionGroups, setProductProvidersOptionGroups] = useState<
    SelectGroupOption[]
  >([]);
  const [displayProductNames, setDisplayProductNames] = useState<SelectOption[]>();

  useEffect(() => {
    // this MUST refresh when mainPlanType changes,
    // otherwise we'll have GIAs showing when ISA is selected
    if (mainPlanType) {
      queryGetProductProviders({
        variables: {
          accountId: validateUUID(accountId),
          productType: sanitiseProductType(mainPlanType),
          productBasis: 'accumulation',
        },
      });
    }
  }, [mainPlanType]);

  const {disableProviderField} = useSPPProviders(
    `${formikKeyPrefix}.planProvider`,
    productProvidersOptionGroups,
    null,
    mainPlanTypeField.value === 'pension',
    false
  );

  useEffect(() => {
    // this MUST refresh when mainPlanType changes,
    // otherwise we'll have GIAs showing when ISA is selected
    if (newPlanFieldObj.value?.planProvider) {
      queryGetProducts({
        variables: {
          productType: sanitiseProductType(mainPlanType),
          providerId: newPlanFieldObj.value?.planProvider,
        },
      });
    }
  }, [
    newPlanFieldObj.value?.planProvider,
    mainPlanType,
    newPlanFieldObj.value?.planType,
  ]);

  useEffect(() => {
    updateNewPlanProducts({
      productNames,
      setFieldValue,
      formikKeyPrefix,
      setDisplayProductNames,
      minor: isMinor,
      currentPlanId: newPlanFieldObj.value?.planType,
      mainPlanType,
    });
  }, [
    productNames,
    mainPlanType,
    newPlanFieldObj.value?.planType,
    isMinor,
    formikKeyPrefix,
  ]);

  useEffect(() => {
    updateNewPlanProviders({
      rawProviders,
      setProductProvidersOptionGroups,
    });
  }, [productProviders]);

  useUpdateIsSaltusProvider({
    isSaltusProviderHelpers,
    productProviders,
    planProvider: newPlanFieldObj.value?.planProvider,
  });

  // const isNewLisa = useDeriveIsNewLisa(formikKeyPrefix);

  const isSaltusSIPP = useMemo(() => {
    const plan = productProviders?.find(
      (x) => x.value === newPlanFieldObj.value?.planProvider
    );
    if (plan) {
      return plan.label.toLocaleLowerCase() === 'saltus sipp';
    }
    return false;
  }, [newPlanFieldObj.value?.planProvider, productProviders]);

  const isSaltus = useMemo(() => {
    const plan = productProviders?.find(
      (x) => x.value === newPlanFieldObj.value?.planProvider
    );
    if (plan) {
      return plan?.isSaltus;
    }
    return false;
  }, [newPlanFieldObj.value?.planProvider, productProviders]);

  useSetDefaultSaltusSippPlanType({displayProductNames, isSaltusSIPP, formikKeyPrefix});

  return (
    <>
      <Grid pt={2} flex={1} container direction="row" spacing={2}>
        <Grid item xs={6}>
          <AutoField
            field={{
              ...recommendPlanProviderField,
              disabled: !productProvidersOptionGroups.length || disableProviderField,
              label: productProvidersOptionGroups.length
                ? recommendPlanProviderField.label
                : 'Loading plan providers...',
              type: ActionType.selectAutocomplete,
              id: `${formikKeyPrefix}.planProvider`,
              options:
                productProviders?.filter((x) => {
                  // ON-7836 - Remove Quilter as provider in OV
                  return !x.label.toLocaleLowerCase().includes('quilter');
                }) || [],
              groupBy: (option) => option.group || '',
            }}
            onBlur={() => {
              clearMandateOnProviderBlur({mandateHelpers});
            }}
          />
        </Grid>
        {isSaltusSIPP ? null : (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...recommendPlanTypeField,
                disabled: !displayProductNames?.length,
                label: recommendPlanTypeField.label,
                id: `${formikKeyPrefix}.planType`,
                options: displayProductNames || [],
              }}
            />
          </Grid>
        )}
        {/* If a transfer to a new plan. Show added amount */}
        {values.planAction === OVRecommendActionEnum.TRANSFER ? (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...recommendAmountField,
                disabled: true,
                id: `transfer.combinedOneOffMovingAmount`,
                label: `Personal Single Amount${
                  values.mainPlanType === 'pension' ? ' (Gross)' : ''
                }`,
              }}
            />
          </Grid>
        ) : null}
        {(!hasWrapper(mainPlanType) || isLowesPension || isTransactBond) && (
          <Grid item xs={6}>
            <MandatesWrapper
              showCurrentMandate={false}
              planProviderOptions={productProviders}
              formikKeyPrefix={formikKeyPrefix}
            />
          </Grid>
        )}
        <Grid item xs={6}>
          <AutoField field={investorCategoryField} />
        </Grid>
        {needsInTrust && !isSPPDA && (
          <Grid item xs={6}>
            <AutoField field={{...inTrustField, id: `${formikKeyPrefix}.inTrust`}} />
          </Grid>
        )}
        {isMinor && (
          <Grid item xs={6}>
            <ParentGuardianSearch formikKeyPrefix={formikKeyPrefix} />
          </Grid>
        )}
        <Grid item xs={12}>
          <PlanFeesField
            inModify={false}
            hasSubplan={hasWrapper(mainPlanType) && !isLowesPension}
            requiresMandate={isSaltus && !hasWrapper(mainPlanType)}
            productId={get(values, `${formikKeyPrefix}.planType`, null)}
            mandateId={
              mandateField && typeof mandateField.value === 'object'
                ? mandateField.value?.id
                : undefined
            }
            providerId={get(values, `${formikKeyPrefix}.planProvider`, null)}
            formikKeyPrefixWithoutFee={formikKeyPrefix}
            // disabled={isNewLisa} // No Ongoing Fee on New LISA
            isSaltus={isSaltus}
          />
        </Grid>
      </Grid>
      <Box mb={2} />
    </>
  );
};
